/**
 * AddMedia — admin-only: upload a video file from this device, or have the
 * server download one from a URL. Both end up as ordinary library titles that
 * go through the same transcode queue and can be deleted from the catalogue.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import type { Download } from '@syncreel/shared';
import { addDownload, getDownloads, removeDownload, uploadVideoFile } from '../api/client';
import { useSessionStore } from '../store/session';
import { Badge, type BadgeVariant } from './ui/Badge';
import { Button } from './ui/Button';
import { useToast } from './ui/Toast';
import styles from './AddMedia.module.css';

export interface AddMediaProps {
  /** Called when a new title was created (upload finished / download completed). */
  onChanged: () => void;
}

const ACCEPT = '.mp4,.mkv,.mov,.avi,.webm,.m4v,.ts,video/*';

function formatBytes(n: number): string {
  const gb = n / 1024 ** 3;
  return gb >= 1 ? `${gb.toFixed(2)} GB` : `${Math.max(0, Math.round(n / 1024 ** 2))} MB`;
}

function badgeFor(d: Download): { variant: BadgeVariant; label: string } {
  switch (d.status) {
    case 'queued':
      return { variant: 'neutral', label: 'Queued' };
    case 'downloading': {
      const pct = d.bytesTotal ? Math.min(99, Math.round((d.bytesDone / d.bytesTotal) * 100)) : null;
      return { variant: 'info', label: pct !== null ? `Downloading ${pct}%` : `Downloading ${formatBytes(d.bytesDone)}` };
    }
    case 'done':
      return { variant: 'ok', label: 'Added' };
    case 'failed':
      return { variant: 'error', label: 'Failed' };
    case 'cancelled':
      return { variant: 'neutral', label: 'Cancelled' };
  }
}

/** Upload + download-by-URL panel (collapsed by default). */
export function AddMedia({ onChanged }: AddMediaProps) {
  const toast = useToast();
  const adminToken = useSessionStore((s) => s.adminToken);
  const [dragging, setDragging] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [uploading, setUploading] = useState<{ name: string; fraction: number } | null>(null);
  const [url, setUrl] = useState('');
  const [adding, setAdding] = useState(false);
  const [downloads, setDownloads] = useState<Download[]>([]);
  const seenDone = useRef<Set<string>>(new Set());
  const hasActive = downloads.some((d) => d.status === 'queued' || d.status === 'downloading');

  const loadDownloads = useCallback(async () => {
    if (!adminToken) return;
    try {
      const res = await getDownloads(adminToken);
      setDownloads(res.downloads);
      let created = false;
      for (const d of res.downloads) {
        if (d.status === 'done' && !seenDone.current.has(d.id)) {
          seenDone.current.add(d.id);
          created = true;
        }
      }
      if (created) onChanged();
    } catch {
      // Best effort; the list refreshes on the next poll.
    }
  }, [adminToken, onChanged]);

  useEffect(() => {
    if (open) void loadDownloads();
  }, [open, loadDownloads]);

  // Poll only while a download is running — an idle panel makes no requests.
  useEffect(() => {
    if (!open || !hasActive) return;
    const t = setInterval(() => void loadDownloads(), 3000);
    return () => clearInterval(t);
  }, [open, hasActive, loadDownloads]);

  async function handleFile(file: File) {
    if (!adminToken) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setUploading({ name: file.name, fraction: 0 });
    try {
      await uploadVideoFile(file, adminToken, (f) => setUploading({ name: file.name, fraction: f }), controller.signal);
      toast.success(`Uploaded "${file.name}" — transcoding started.`);
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      abortRef.current = null;
      setUploading(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function handleAddUrl(e: FormEvent) {
    e.preventDefault();
    if (!adminToken || url.trim() === '') return;
    setAdding(true);
    try {
      await addDownload(url.trim(), adminToken);
      setUrl('');
      toast.success('Download queued.');
      await loadDownloads();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not queue the download.');
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove(d: Download) {
    if (!adminToken) return;
    try {
      await removeDownload(d.id, adminToken);
      setDownloads((prev) => prev.filter((x) => x.id !== d.id));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove.');
    }
  }

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <Button variant="secondary" size="sm" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          {open ? 'Hide upload / download' : 'Upload or download a video'}
        </Button>
      </div>
      {open ? (
        <>
          <div className={styles.grid}>
          <div className={styles.block}>
            <span className={styles.label}>Upload from this device</span>
            <label
              className={`${styles.drop} ${dragging ? styles.dropActive : ''} ${uploading ? styles.dropBusy : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                if (!uploading) setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                const f = e.dataTransfer.files?.[0];
                if (f && !uploading) void handleFile(f);
              }}
            >
              <svg className={styles.dropIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 3 7 8h3.5v6h3V8H17zM5 17v3h14v-3h-2v1H7v-1z" />
              </svg>
              <span className={styles.dropTitle}>{dragging ? 'Drop to upload' : 'Choose a video or drop it here'}</span>
              <span className={styles.meta}>MP4, MKV, MOV, AVI, WebM</span>
              <input
                ref={fileRef}
                className="sr-only"
                type="file"
                accept={ACCEPT}
                disabled={uploading !== null}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleFile(f);
                }}
                aria-label="Choose a video file to upload"
              />
            </label>
            {uploading ? (
              <div className={styles.progressRow}>
                <div className={styles.bar} role="progressbar" aria-valuenow={Math.round(uploading.fraction * 100)} aria-valuemin={0} aria-valuemax={100}>
                  <div className={styles.fill} style={{ width: `${Math.round(uploading.fraction * 100)}%` }} />
                </div>
                <span className={styles.meta}>
                  {uploading.name} — {Math.round(uploading.fraction * 100)}%
                </span>
                <Button variant="ghost" size="sm" onClick={() => abortRef.current?.abort()}>
                  Cancel
                </Button>
              </div>
            ) : null}
          </div>

          <form className={styles.block} onSubmit={(e) => void handleAddUrl(e)}>
            <span className={styles.label}>Download from a URL</span>
            <div className={styles.urlRow}>
              <input
                className={styles.input}
                type="url"
                placeholder="https://example.com/movie.mkv"
                value={url}
                maxLength={2048}
                onChange={(e) => setUrl(e.target.value)}
                aria-label="Video URL"
              />
              <Button type="submit" loading={adding} disabled={url.trim() === ''}>
                Download
              </Button>
            </div>
            <span className={styles.meta}>Direct http(s) links to .mp4/.mkv/.mov/.avi/.webm/.m4v/.ts files. Private/internal addresses are blocked.</span>
          </form>
          </div>

          {downloads.length > 0 ? (
            <ul className={styles.list}>
              {downloads.map((d) => {
                const b = badgeFor(d);
                return (
                  <li key={d.id} className={styles.row}>
                    <div className={styles.info}>
                      <span className={styles.name} title={d.url}>
                        {d.url}
                      </span>
                      {d.error ? <span className={styles.error}>{d.error}</span> : null}
                    </div>
                    <Badge variant={b.variant} label={b.label} />
                    <Button variant="ghost" size="sm" onClick={() => void handleRemove(d)}>
                      {d.status === 'queued' || d.status === 'downloading' ? 'Cancel' : 'Clear'}
                    </Button>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
